import React from 'react';
import {
  Box, Container, Typography, Slider, useTheme, useMediaQuery,
  Tabs, Tab, Button, Tooltip, IconButton, Paper, TextField,
  FormControlLabel, Switch, List, ListItem, ListItemText,
  ListItemSecondaryAction, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Collapse,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
  ReferenceLine, ReferenceDot, PieChart, Pie, Cell, Legend,
} from 'recharts';

import { useEtfCalculator } from '../hooks/useEtfCalculator';
import {
  formatCurrency, predefinedScenarios,
  CRYPTO_SCENARIO_RATES, CryptoScenario, calculateMonthlyAnnuity, MAX_AGE,
} from '../utils/calculation';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`calculator-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ASSET_COLORS = {
  etf: '#10B981',
  cash: '#3B82F6',
  crypto: '#F59E0B',
  realEstate: '#8B5CF6',
  bonds: '#EC4899',
};

const ETFCalculator: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const calc = useEtfCalculator();
  const [assetsOpen, setAssetsOpen] = React.useState(false);
  const [nettoDetailsOpen, setNettoDetailsOpen] = React.useState(false);

  const tip = (text: string) => (
    <Tooltip title={text}>
      <IconButton size="small" sx={{ ml: 0.5, color: 'primary.main' }}><InfoIcon fontSize="small" /></IconButton>
    </Tooltip>
  );

  // Retirement-age data point for the donut chart
  const retirementIdx = calc.chartData.findIndex((d) => d.age === calc.targetRetirementAge);
  const retData = retirementIdx >= 0 ? calc.chartData[retirementIdx] : undefined;
  const lastData = calc.chartData[calc.chartData.length - 1];

  const donutData = retData ? [
    { name: 'ETF', value: retData.etfValue, color: ASSET_COLORS.etf },
    { name: 'Cash', value: retData.cashValue, color: ASSET_COLORS.cash },
    ...(calc.cryptoEnabled ? [{ name: 'Krypto', value: retData.cryptoValue, color: ASSET_COLORS.crypto }] : []),
    ...(calc.realEstateEnabled ? [{ name: 'Immobilie', value: retData.realEstateValue, color: ASSET_COLORS.realEstate }] : []),
    ...(calc.bondsEnabled ? [{ name: 'Anleihen', value: retData.bondsValue, color: ASSET_COLORS.bonds }] : []),
  ].filter((d) => d.value > 0) : [];

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 0.5, sm: 3 } }}>
      <Typography variant="h4" component="h1" gutterBottom>Portfolio Vermögensrechner</Typography>

      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
        {/* ── Stacked Area Chart ── */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ pl: 0, pr: 0, pt: { xs: 1, sm: 2, md: 3 }, pb: { xs: 1, sm: 2, md: 3 }, mb: { xs: 2, md: 4 }, mx: { xs: 0, sm: 2 }, bgcolor: 'background.paper', borderRadius: 3, width: '100%' }}>
            <Box sx={{ height: { xs: 300, sm: 320, md: 400 }, width: '100%' }}>
              <ResponsiveContainer>
                <AreaChart data={calc.chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="age" label={{ value: 'Alter', position: 'bottom', offset: 10 }} stroke="#94A3B8" fontSize={12} />
                  <YAxis
                    tickFormatter={(value) => {
                      const short = window.innerWidth < 600;
                      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M${short ? '' : ' €'}`;
                      if (value >= 1000) return `${(value / 1000).toFixed(0)}k${short ? '' : ' €'}`;
                      return short ? `${value}` : `${value.toLocaleString('de-DE')} €`;
                    }}
                    stroke="#94A3B8" fontSize={window.innerWidth < 600 ? 10 : 11} interval={0} domain={[0, calc.maxY]}
                  />
                  <RechartsTooltip
                    formatter={(value: number, name: string) => [formatCurrency(value), name]}
                    labelFormatter={(value) => `Alter: ${value}`}
                    contentStyle={{ backgroundColor: '#1E293B', border: 'none', borderRadius: 8 }}
                  />
                  <ReferenceLine y={calc.zielLinieY} stroke="#EF4444" strokeDasharray="3 3"
                    label={{ value: `Zielkapital: ${formatCurrency(calc.zielLinieY)}`, position: 'insideTopRight', fill: '#EF4444', fontSize: 12, dx: -16, dy: 8 }}
                  />
                  <Area type="monotone" dataKey="etfValue" stackId="portfolio" stroke={ASSET_COLORS.etf} fill={ASSET_COLORS.etf} fillOpacity={0.6} name="ETF" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="cashValue" stackId="portfolio" stroke={ASSET_COLORS.cash} fill={ASSET_COLORS.cash} fillOpacity={0.6} name="Cash" strokeWidth={1.5} />
                  {calc.cryptoEnabled && <Area type="monotone" dataKey="cryptoValue" stackId="portfolio" stroke={ASSET_COLORS.crypto} fill={ASSET_COLORS.crypto} fillOpacity={0.6} name="Krypto" strokeWidth={1.5} />}
                  {calc.realEstateEnabled && <Area type="monotone" dataKey="realEstateValue" stackId="portfolio" stroke={ASSET_COLORS.realEstate} fill={ASSET_COLORS.realEstate} fillOpacity={0.6} name="Immobilie" strokeWidth={1.5} />}
                  {calc.bondsEnabled && <Area type="monotone" dataKey="bondsValue" stackId="portfolio" stroke={ASSET_COLORS.bonds} fill={ASSET_COLORS.bonds} fillOpacity={0.6} name="Anleihen" strokeWidth={1.5} />}
                  {calc.intersection && <ReferenceDot x={calc.intersection.age} y={calc.intersection.totalValue} r={8} fill="#EF4444" stroke="#fff" strokeWidth={2} isFront />}
                </AreaChart>
              </ResponsiveContainer>
            </Box>

            {/* Legend */}
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              {[
                { color: ASSET_COLORS.etf, label: 'ETF' },
                { color: ASSET_COLORS.cash, label: 'Cash' },
                ...(calc.cryptoEnabled ? [{ color: ASSET_COLORS.crypto, label: 'Krypto' }] : []),
                ...(calc.realEstateEnabled ? [{ color: ASSET_COLORS.realEstate, label: 'Immobilie' }] : []),
                ...(calc.bondsEnabled ? [{ color: ASSET_COLORS.bonds, label: 'Anleihen' }] : []),
              ].map(({ color, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 16, height: 4, bgcolor: color, borderRadius: 2, mr: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Target info + Zielkapital-Erklärung */}
            {calc.intersection ? (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, mb: 1, gap: 1 }}>
                <Box sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 2, textAlign: 'center', color: '#fff', boxShadow: 3, fontWeight: 500 }}>
                  <strong>Ziel erreicht ab Alter:</strong> {calc.intersection.age}<br />
                  <strong>Vermögen:</strong> {formatCurrency(calc.intersection.totalValue)}<br />
                  <strong>Damit reicht die gewünschte Rente bis {MAX_AGE}.</strong>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 600, textAlign: 'center' }}>
                  Zielkapital = {formatCurrency(calc.zielLinieY)} — berechnet als Barwert der Rentenentnahme von {formatCurrency(calc.desiredMonthlyIncome)}/Monat
                  {calc.statutoryPension > 0 && <> (abzgl. {formatCurrency(calc.statutoryPension)} gesetzl. Rente)</>}
                  {' '}über {MAX_AGE - calc.targetRetirementAge} Jahre bei {calc.returnRate}% Rendite.
                  Formel: K = Entnahme × (1 − (1+r)⁻ⁿ) / r
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, mb: 1, gap: 1 }}>
                <Box sx={{ p: 2, bgcolor: 'grey.800', borderRadius: 2, textAlign: 'center', color: '#fff', boxShadow: 3, fontWeight: 500 }}>
                  <strong>Das Zielkapital von {formatCurrency(calc.zielLinieY)} wird mit den aktuellen Einstellungen nicht erreicht.</strong>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 600, textAlign: 'center' }}>
                  Benötigtes Kapital für {formatCurrency(calc.desiredMonthlyIncome)}/Monat
                  {calc.statutoryPension > 0 && <> (abzgl. {formatCurrency(calc.statutoryPension)} gesetzl. Rente)</>}
                  {' '}über {MAX_AGE - calc.targetRetirementAge} Jahre bei {calc.returnRate}% Rendite.
                  Formel: K = Entnahme × (1 − (1+r)⁻ⁿ) / r
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Grundeinstellungen ── */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mx: { xs: 0, sm: 2 }, bgcolor: 'background.paper', borderRadius: 3, width: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Grundeinstellungen</Typography>
            <Grid container spacing={2} columns={isMobile ? 1 : 12}>
              {/* Alter */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="Aktuelles Alter" tooltip="Ihr aktuelles Alter" value={`${calc.currentAge} Jahre`}
                  sliderProps={{ value: calc.localCurrentAge, onChange: (_, v) => calc.setLocalCurrentAge(v as number), onChangeCommitted: (_, v) => calc.setCurrentAge(v as number), min: 0, max: 90 }}
                  tip={tip}
                />
              </Grid>
              {/* Brutto-Gehalt + Netto (klappbar) */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="Brutto-Gehalt / Monat" tooltip="Monatliches Bruttogehalt (Steuerklasse 1, BaWü, keine Kirchensteuer, kinderlos)" value={formatCurrency(calc.grossMonthlySalary)}
                  sliderProps={{ value: calc.localGrossMonthlySalary, onChange: (_, v) => calc.setLocalGrossMonthlySalary(v as number), onChangeCommitted: (_, v) => calc.setGrossMonthlySalary(v as number), min: 0, max: 15000, step: 100, valueLabelFormat: (v: number) => formatCurrency(v) }}
                  tip={tip}
                />
                {calc.grossMonthlySalary > 0 && (
                  <Box sx={{ mt: -1, px: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setNettoDetailsOpen(!nettoDetailsOpen)}>
                      <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ flexGrow: 1 }}>
                        Netto: <span style={{ color: '#22C55E' }}>{formatCurrency(calc.netSalary.netMonthly)}</span>
                        {' · '}Zum Leben: <span style={{ color: calc.availableAfterInvesting >= 0 ? '#22C55E' : '#EF4444' }}>{formatCurrency(calc.availableAfterInvesting)}</span>
                      </Typography>
                      {nettoDetailsOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                    </Box>
                    <Collapse in={nettoDetailsOpen}>
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" color="text.secondary">Lohnsteuer</Typography>
                          <Typography variant="caption" color="error.light">−{formatCurrency(calc.netSalary.incomeTaxMonthly)}</Typography>
                        </Box>
                        {calc.netSalary.soliMonthly > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="caption" color="text.secondary">Soli</Typography>
                            <Typography variant="caption" color="error.light">−{formatCurrency(calc.netSalary.soliMonthly)}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" color="text.secondary">Rentenversicherung</Typography>
                          <Typography variant="caption" color="error.light">−{formatCurrency(calc.netSalary.rvMonthly)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" color="text.secondary">Krankenversicherung</Typography>
                          <Typography variant="caption" color="error.light">−{formatCurrency(calc.netSalary.kvMonthly)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" color="text.secondary">Pflegeversicherung</Typography>
                          <Typography variant="caption" color="error.light">−{formatCurrency(calc.netSalary.pvMonthly)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" color="text.secondary">Arbeitslosenvers.</Typography>
                          <Typography variant="caption" color="error.light">−{formatCurrency(calc.netSalary.avMonthly)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">− ETF-Sparrate</Typography>
                          <Typography variant="caption" color="text.secondary">−{formatCurrency(calc.monthlyInvestment)}</Typography>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Grid>
              {/* ETF Startwert */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="ETF Startwert" tooltip="Bereits in ETFs investiertes Kapital" value={formatCurrency(calc.etfStartValue)}
                  sliderProps={{ value: calc.localEtfStartValue, onChange: (_, v) => calc.setLocalEtfStartValue(v as number), onChangeCommitted: (_, v) => calc.setEtfStartValue(v as number), min: 0, max: 200000, step: 1000, valueLabelFormat: (v: number) => formatCurrency(v) }}
                  tip={tip}
                />
              </Grid>
              {/* Cash */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="Cash / Tagesgeld" tooltip="Barvermögen auf Tagesgeld oder Sparkonto" value={formatCurrency(calc.cashStartAmount)}
                  sliderProps={{ value: calc.localCashStartAmount, onChange: (_, v) => calc.setLocalCashStartAmount(v as number), onChangeCommitted: (_, v) => calc.setCashStartAmount(v as number), min: 0, max: 200000, step: 1000, valueLabelFormat: (v: number) => formatCurrency(v) }}
                  tip={tip}
                />
              </Grid>
              {/* Cash Rate */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="Tagesgeld-Zinssatz" tooltip="Jährlicher Zinssatz auf das Cash-Vermögen" value={`${calc.cashAnnualRate.toFixed(1)} %`}
                  sliderProps={{ value: calc.localCashAnnualRate, onChange: (_, v) => calc.setLocalCashAnnualRate(v as number), onChangeCommitted: (_, v) => calc.setCashAnnualRate(v as number), min: 0, max: 6, step: 0.1 }}
                  tip={tip}
                />
              </Grid>
              {/* Monthly */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="Monatliche ETF-Einzahlung" value={formatCurrency(calc.monthlyInvestment)}
                  sliderProps={{ value: calc.localMonthlyInvestment, onChange: (_, v) => calc.setLocalMonthlyInvestment(v as number), onChangeCommitted: (_, v) => calc.setMonthlyInvestment(v as number), min: 0, max: 2000, step: 50, valueLabelFormat: (v: number) => formatCurrency(v) }}
                  tip={tip}
                />
              </Grid>
              {/* Contribution years */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="Einzahlungszeitraum" value={`${calc.contributionYears} Jahre`}
                  sliderProps={{ value: Math.min(Math.max(calc.localContributionYears, 1), Math.max(1, calc.targetRetirementAge - calc.currentAge)), onChange: (_, v) => calc.setLocalContributionYears(v as number), onChangeCommitted: (_, v) => calc.setContributionYears(Math.max(1, v as number)), min: 1, max: Math.max(1, calc.targetRetirementAge - calc.currentAge) }}
                  tip={tip}
                />
              </Grid>
              {/* Return rate */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="ETF-Rendite p.a." tooltip="Erwartete jährliche Rendite" value={`${calc.returnRate.toFixed(1)} %`}
                  sliderProps={{ value: calc.localReturnRate, onChange: (_, v) => calc.setLocalReturnRate(v as number), onChangeCommitted: (_, v) => calc.setReturnRate(v as number), min: 1, max: 12, step: 0.1 }}
                  tip={tip}
                />
              </Grid>
              {/* Retirement age */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" component="span" color="text.secondary" fontSize={{ xs: 14, sm: 16 }}>Gewünschtes Rentenalter</Typography>
                    {tip("Alter, in dem Sie in Rente gehen möchten")}
                  </Box>
                  <Typography color="primary" variant="h6" gutterBottom fontSize={{ xs: 18, sm: 22 }}>{calc.targetRetirementAge} Jahre</Typography>
                  <Box sx={{ px: 2 }}>
                    <Slider value={Math.max(calc.targetRetirementAge, Math.max(calc.currentAge + 1, 30))}
                      onChange={(_, v) => { calc.setTargetRetirementAge(v as number); calc.setRetirementAgeManuallySet(true); }}
                      min={Math.max(calc.currentAge + 1, 30)} max={90} marks valueLabelDisplay="auto"
                      sx={{ '& .MuiSlider-rail': { opacity: 0.3 }, height: { xs: 2, sm: 4 }, my: 2 }}
                    />
                  </Box>
                  {calc.retirementAgeManuallySet && (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Button variant="outlined" size="small" onClick={() => calc.setRetirementAgeManuallySet(false)}>Auto-Berechnung</Button>
                    </Box>
                  )}
                </Box>
              </Grid>
              {/* Desired income */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SliderBlock label="Monatl. Renteneinkommen" tooltip="Gewünschte monatliche Entnahme im Ruhestand" value={formatCurrency(calc.desiredMonthlyIncome)}
                  sliderProps={{ value: calc.localDesiredMonthlyIncome, onChange: (_, v) => calc.setLocalDesiredMonthlyIncome(v as number), onChangeCommitted: (_, v) => calc.setDesiredMonthlyIncome(v as number), min: 500, max: 5000, step: 50, valueLabelFormat: (v: number) => formatCurrency(v) }}
                  tip={tip}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ── Weitere Assets ── */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mx: { xs: 0, sm: 2 }, bgcolor: 'background.paper', borderRadius: 3, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setAssetsOpen(!assetsOpen)}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Weitere Assets</Typography>
              {assetsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={assetsOpen}>
              <Grid container spacing={3} sx={{ mt: 1 }}>

                {/* ── Krypto ── */}
                <Grid item xs={12} md={6} lg={4}>
                  <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(245,158,11,0.05)', borderRadius: 2, border: '1px solid', borderColor: calc.cryptoEnabled ? ASSET_COLORS.crypto : 'divider' }}>
                    <FormControlLabel
                      control={<Switch checked={calc.cryptoEnabled} onChange={(e) => calc.setCryptoEnabled(e.target.checked)} color="warning" />}
                      label={<Typography variant="subtitle1" fontWeight={600}>Bitcoin / Krypto</Typography>}
                    />
                    <Collapse in={calc.cryptoEnabled}>
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>BTC-Menge</Typography>
                          <Typography color="warning.main" variant="h6" fontSize={18}>{calc.btcAmount.toFixed(4)} BTC</Typography>
                          <Slider value={calc.localBtcAmount} onChange={(_, v) => calc.setLocalBtcAmount(v as number)} onChangeCommitted={(_, v) => calc.setBtcAmount(v as number)} min={0} max={5} step={0.01} valueLabelDisplay="auto" valueLabelFormat={(v) => `${v.toFixed(2)} BTC`} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Einstiegspreis / BTC</Typography>
                          <Typography color="warning.main" variant="h6" fontSize={18}>{formatCurrency(calc.btcBuyPrice)}</Typography>
                          <Slider value={calc.localBtcBuyPrice} onChange={(_, v) => calc.setLocalBtcBuyPrice(v as number)} onChangeCommitted={(_, v) => calc.setBtcBuyPrice(v as number)} min={1000} max={150000} step={1000} valueLabelDisplay="auto" valueLabelFormat={(v) => formatCurrency(v)} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Szenario</Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {(['bull', 'base', 'bear'] as CryptoScenario[]).map((s) => (
                              <Chip key={s}
                                label={`${s === 'bull' ? 'Bull' : s === 'base' ? 'Base' : 'Bear'} (${CRYPTO_SCENARIO_RATES[s] > 0 ? '+' : ''}${CRYPTO_SCENARIO_RATES[s]}% p.a.)`}
                                variant={calc.cryptoScenario === s ? 'filled' : 'outlined'}
                                color={calc.cryptoScenario === s ? 'warning' : 'default'}
                                onClick={() => calc.setCryptoScenario(s)}
                              />
                            ))}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Startwert: {formatCurrency(calc.btcAmount * calc.btcBuyPrice)}</Typography>
                        </Grid>
                      </Grid>
                    </Collapse>
                  </Paper>
                </Grid>

                {/* ── Immobilie ── */}
                <Grid item xs={12} md={6} lg={4}>
                  <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(139,92,246,0.05)', borderRadius: 2, border: '1px solid', borderColor: calc.realEstateEnabled ? ASSET_COLORS.realEstate : 'divider' }}>
                    <FormControlLabel
                      control={<Switch checked={calc.realEstateEnabled} onChange={(e) => calc.setRealEstateEnabled(e.target.checked)} color="secondary" />}
                      label={<Typography variant="subtitle1" fontWeight={600}>Immobilie</Typography>}
                    />
                    <Collapse in={calc.realEstateEnabled}>
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Kaufpreis</Typography>
                          <Typography color="secondary" variant="h6" fontSize={18}>{formatCurrency(calc.purchasePrice)}</Typography>
                          <Slider value={calc.localPurchasePrice} onChange={(_, v) => calc.setLocalPurchasePrice(v as number)} onChangeCommitted={(_, v) => calc.setPurchasePrice(v as number)} min={50000} max={1000000} step={10000} valueLabelDisplay="auto" valueLabelFormat={(v) => formatCurrency(v)} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Wertsteigerung p.a.</Typography>
                          <Typography color="secondary" variant="h6" fontSize={18}>{calc.annualAppreciation.toFixed(1)} %</Typography>
                          <Slider value={calc.localAnnualAppreciation} onChange={(_, v) => calc.setLocalAnnualAppreciation(v as number)} onChangeCommitted={(_, v) => calc.setAnnualAppreciation(v as number)} min={0} max={8} step={0.1} valueLabelDisplay="auto" sx={{ mt: 1 }} />
                        </Grid>

                        {/* Vermietung */}
                        <Grid item xs={12}><FormControlLabel control={<Switch checked={calc.isRental} onChange={(e) => calc.setIsRental(e.target.checked)} />} label="Vermietung" /></Grid>
                        <Collapse in={calc.isRental} sx={{ width: '100%' }}>
                          <Grid container spacing={2} sx={{ pl: 2, pr: 2, pt: 1 }}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>Monatl. Kaltmiete</Typography>
                              <Typography color="secondary" variant="h6" fontSize={18}>{formatCurrency(calc.monthlyRent)}</Typography>
                              <Slider value={calc.localMonthlyRent} onChange={(_, v) => calc.setLocalMonthlyRent(v as number)} onChangeCommitted={(_, v) => calc.setMonthlyRent(v as number)} min={0} max={3000} step={50} valueLabelDisplay="auto" valueLabelFormat={(v) => formatCurrency(v)} sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>Monatl. Kosten</Typography>
                              <Typography color="secondary" variant="h6" fontSize={18}>{formatCurrency(calc.monthlyExpenses)}</Typography>
                              <Slider value={calc.localMonthlyExpenses} onChange={(_, v) => calc.setLocalMonthlyExpenses(v as number)} onChangeCommitted={(_, v) => calc.setMonthlyExpenses(v as number)} min={0} max={1500} step={25} valueLabelDisplay="auto" valueLabelFormat={(v) => formatCurrency(v)} sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">Netto-Mieteinnahmen: {formatCurrency(Math.max(0, calc.monthlyRent - calc.monthlyExpenses))} / Monat</Typography></Grid>
                          </Grid>
                        </Collapse>

                        {/* Finanzierung */}
                        <Grid item xs={12}><FormControlLabel control={<Switch checked={calc.hasLoan} onChange={(e) => calc.setHasLoan(e.target.checked)} />} label="Finanzierung / Kredit" /></Grid>
                        <Collapse in={calc.hasLoan} sx={{ width: '100%' }}>
                          <Grid container spacing={2} sx={{ pl: 2, pr: 2, pt: 1 }}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>Kreditsumme</Typography>
                              <Typography color="secondary" variant="h6" fontSize={18}>{formatCurrency(calc.loanAmount)}</Typography>
                              <Slider value={calc.localLoanAmount} onChange={(_, v) => calc.setLocalLoanAmount(v as number)} onChangeCommitted={(_, v) => calc.setLoanAmount(v as number)} min={0} max={900000} step={10000} valueLabelDisplay="auto" valueLabelFormat={(v) => formatCurrency(v)} sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>Kreditzins p.a.</Typography>
                              <Typography color="secondary" variant="h6" fontSize={18}>{calc.loanInterestRate.toFixed(1)} %</Typography>
                              <Slider value={calc.localLoanInterestRate} onChange={(_, v) => calc.setLocalLoanInterestRate(v as number)} onChangeCommitted={(_, v) => calc.setLoanInterestRate(v as number)} min={0.5} max={8} step={0.1} valueLabelDisplay="auto" sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>Laufzeit</Typography>
                              <Typography color="secondary" variant="h6" fontSize={18}>{calc.loanTermYears} Jahre</Typography>
                              <Slider value={calc.localLoanTermYears} onChange={(_, v) => calc.setLocalLoanTermYears(v as number)} onChangeCommitted={(_, v) => calc.setLoanTermYears(v as number)} min={5} max={40} step={1} valueLabelDisplay="auto" sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Monatl. Rate: {formatCurrency(calculateMonthlyAnnuity(calc.loanAmount, calc.loanInterestRate, calc.loanTermYears))}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Collapse>
                      </Grid>
                    </Collapse>
                  </Paper>
                </Grid>

                {/* ── Anleihen ── */}
                <Grid item xs={12} md={6} lg={4}>
                  <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(236,72,153,0.05)', borderRadius: 2, border: '1px solid', borderColor: calc.bondsEnabled ? ASSET_COLORS.bonds : 'divider' }}>
                    <FormControlLabel
                      control={<Switch checked={calc.bondsEnabled} onChange={(e) => calc.setBondsEnabled(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ASSET_COLORS.bonds }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ASSET_COLORS.bonds } }} />}
                      label={<Typography variant="subtitle1" fontWeight={600}>Anleihen / Bonds</Typography>}
                    />
                    <Collapse in={calc.bondsEnabled}>
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Investiertes Kapital</Typography>
                          <Typography sx={{ color: ASSET_COLORS.bonds }} variant="h6" fontSize={18}>{formatCurrency(calc.bondsStartAmount)}</Typography>
                          <Slider value={calc.localBondsStartAmount} onChange={(_, v) => calc.setLocalBondsStartAmount(v as number)} onChangeCommitted={(_, v) => calc.setBondsStartAmount(v as number)} min={0} max={200000} step={1000} valueLabelDisplay="auto" valueLabelFormat={(v) => formatCurrency(v)} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Kuponrate p.a.</Typography>
                          <Typography sx={{ color: ASSET_COLORS.bonds }} variant="h6" fontSize={18}>{calc.bondsCouponRate.toFixed(1)} %</Typography>
                          <Slider value={calc.localBondsCouponRate} onChange={(_, v) => calc.setLocalBondsCouponRate(v as number)} onChangeCommitted={(_, v) => calc.setBondsCouponRate(v as number)} min={0} max={10} step={0.1} valueLabelDisplay="auto" sx={{ mt: 1 }} />
                        </Grid>
                      </Grid>
                    </Collapse>
                  </Paper>
                </Grid>

              </Grid>
            </Collapse>
          </Paper>
        </Grid>

        {/* ── Zusammenfassung + Donut ── */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mx: { xs: 0, sm: 2 }, bgcolor: 'background.paper', borderRadius: 3, width: '100%' }}>
            <Grid container spacing={2}>
              {/* Summary numbers */}
              <Grid item xs={12} md={donutData.length > 1 ? 7 : 12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
                      <Typography variant="h6" color="textSecondary" gutterBottom fontSize={{ xs: 14, sm: 18 }}>Gesamtinvestition</Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', fontSize: { xs: 20, sm: 32 } }}>{formatCurrency(lastData?.totalInvestment || 0)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
                      <Typography variant="h6" color="textSecondary" gutterBottom fontSize={{ xs: 14, sm: 18 }}>Endkapital</Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', fontSize: { xs: 20, sm: 32 } }}>{formatCurrency(lastData?.totalValue || 0)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
                      <Typography variant="h6" color="textSecondary" gutterBottom fontSize={{ xs: 14, sm: 18 }}>Gewinn</Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', fontSize: { xs: 20, sm: 32 } }}>{formatCurrency((lastData?.totalValue || 0) - (lastData?.totalInvestment || 0))}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Gesetzliche Rente Info */}
                {calc.grossMonthlySalary > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                          <Typography variant="caption" color="text.secondary">Gesetzl. Rente (geschätzt)</Typography>
                          <Typography variant="h6" sx={{ color: '#22C55E', fontWeight: 600 }}>{formatCurrency(calc.statutoryPension)} / Monat</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                          <Typography variant="caption" color="text.secondary">Verbleibende Entnahme aus Portfolio</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatCurrency(Math.max(0, calc.desiredMonthlyIncome - calc.statutoryPension))} / Monat</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                          <Typography variant="caption" color="text.secondary">Einkommensersatzquote</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {calc.grossMonthlySalary > 0 ? Math.round(((calc.statutoryPension + Math.max(0, calc.desiredMonthlyIncome - calc.statutoryPension)) / calc.grossMonthlySalary) * 100) : 0}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Asset breakdown table */}
                {retData && (retData.cashValue > 0 || retData.cryptoValue > 0 || retData.realEstateValue > 0 || retData.bondsValue > 0) && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom align="center">Aufschlüsselung bei Rentenalter ({calc.targetRetirementAge})</Typography>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      {[
                        { label: 'ETF', value: retData.etfValue, color: ASSET_COLORS.etf },
                        { label: 'Cash', value: retData.cashValue, color: ASSET_COLORS.cash },
                        ...(calc.cryptoEnabled ? [{ label: 'Krypto', value: retData.cryptoValue, color: ASSET_COLORS.crypto }] : []),
                        ...(calc.realEstateEnabled ? [{ label: 'Immobilie', value: retData.realEstateValue, color: ASSET_COLORS.realEstate }] : []),
                        ...(calc.bondsEnabled ? [{ label: 'Anleihen', value: retData.bondsValue, color: ASSET_COLORS.bonds }] : []),
                      ].filter((a) => a.value > 0).map(({ label, value, color }) => (
                        <Grid item xs={6} sm={3} key={label}>
                          <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '50%', display: 'inline-block', mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body1" fontWeight={600}>{formatCurrency(value)}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Grid>

              {/* Donut chart */}
              {donutData.length > 1 && (
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" color="text.secondary" align="center" gutterBottom>Asset-Allocation bei Rentenalter</Typography>
                  <Box sx={{ height: 250, width: '100%' }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={12}>
                          {donutData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <RechartsTooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: '#1E293B', border: 'none', borderRadius: 8 }} />
                        <Legend formatter={(value) => <span style={{ color: '#94A3B8', fontSize: 12 }}>{value}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* ── Analyse ── */}
        {retData && calc.chartData.length > 1 && (() => {
          const totalInvested = retData.totalInvestment;
          const portfolioAtRetirement = retData.totalValue;
          const totalGain = portfolioAtRetirement - totalInvested;
          const gainRatio = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;
          const savingsRate = calc.netSalary.netMonthly > 0 ? (calc.monthlyInvestment / calc.netSalary.netMonthly) * 100 : 0;
          const yearsToRetirement = calc.targetRetirementAge - calc.currentAge;
          const totalContributions = calc.monthlyInvestment * 12 * Math.min(calc.contributionYears, yearsToRetirement);
          const compoundGain = portfolioAtRetirement - totalInvested;
          const netPortfolioWithdrawal = Math.max(0, calc.desiredMonthlyIncome - calc.statutoryPension);
          const rentalIncome = calc.realEstateEnabled && calc.isRental ? Math.max(0, calc.monthlyRent - calc.monthlyExpenses) : 0;
          const effectiveMonthlyWithdrawal = Math.max(0, netPortfolioWithdrawal - rentalIncome);
          const liquidAssets = retData.etfValue + retData.cashValue + retData.cryptoValue + retData.bondsValue;
          const yearsLiquidLasts = effectiveMonthlyWithdrawal > 0 ? liquidAssets / (effectiveMonthlyWithdrawal * 12) : Infinity;
          const lastPoint = calc.chartData[calc.chartData.length - 1];
          const portfolioSurvives = lastPoint.totalValue > 0;

          return (
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mx: { xs: 0, sm: 2 }, bgcolor: 'background.paper', borderRadius: 3, width: '100%' }}>
                <Typography variant="h6" gutterBottom>Analyse</Typography>
                <Grid container spacing={2}>

                  {/* Sparquote */}
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(59,130,246,0.08)', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Sparquote</Typography>
                      <Typography variant="h5" fontWeight={700} color={savingsRate > 20 ? '#22C55E' : savingsRate > 10 ? '#F59E0B' : '#EF4444'}>
                        {savingsRate.toFixed(0)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">vom Netto</Typography>
                    </Box>
                  </Grid>

                  {/* Einzahlung vs. Zinseszins */}
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(16,185,129,0.08)', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Zinseszins-Effekt</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#10B981' }}>
                        {gainRatio.toFixed(0)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Gewinn auf Einzahlung</Typography>
                    </Box>
                  </Grid>

                  {/* Eingezahlt vs. Gewinn */}
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(139,92,246,0.08)', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Eingezahlt</Typography>
                      <Typography variant="body1" fontWeight={600}>{formatCurrency(totalContributions)}</Typography>
                      <Typography variant="caption" color="text.secondary">Zinseszins</Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ color: '#10B981' }}>{formatCurrency(compoundGain)}</Typography>
                    </Box>
                  </Grid>

                  {/* Entnahme-Details */}
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(245,158,11,0.08)', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Monatl. Einkommen im Alter</Typography>
                      <Typography variant="body2">{formatCurrency(calc.statutoryPension)} Rente</Typography>
                      {rentalIncome > 0 && <Typography variant="body2">{formatCurrency(rentalIncome)} Miete</Typography>}
                      <Typography variant="body2">{formatCurrency(effectiveMonthlyWithdrawal)} Portfolio</Typography>
                      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5, pt: 0.5 }}>
                        <Typography variant="body1" fontWeight={700}>{formatCurrency(calc.desiredMonthlyIncome)} gesamt</Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Nachhaltigkeit */}
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: portfolioSurvives ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Reicht bis Alter</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: portfolioSurvives ? '#22C55E' : '#EF4444' }}>
                        {portfolioSurvives ? `${MAX_AGE}+` : Math.min(MAX_AGE, calc.targetRetirementAge + Math.floor(yearsLiquidLasts))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {portfolioSurvives ? 'Portfolio überlebt' : `Liquidität: ${yearsLiquidLasts.toFixed(1)} J.`}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Vermögen bei 85 */}
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(59,130,246,0.08)', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Restvermögen mit {MAX_AGE}</Typography>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {formatCurrency(lastPoint.totalValue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        real: {formatCurrency(lastPoint.realValue || 0)}
                      </Typography>
                    </Box>
                  </Grid>

                </Grid>
              </Paper>
            </Grid>
          );
        })()}

        {/* ── Tabs ── */}
        <Grid item xs={12}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={calc.tabValue} onChange={calc.handleTabChange}>
              <Tab label="Szenario-Vergleich" />
              <Tab label="Inflationsrechner" />
              <Tab label="Steueroptimierung" />
            </Tabs>
          </Box>

          <TabPanel value={calc.tabValue} index={0}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Vordefinierte Szenarien</Typography>
              <Grid container spacing={2}>
                {predefinedScenarios.map((scenario) => (
                  <Grid item xs={12} sm={4} key={scenario.name}>
                    <Paper elevation={2} sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, height: '100%' }} onClick={() => calc.applyPredefinedScenario(scenario)}>
                      <Typography variant="h6" gutterBottom>{scenario.name}</Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>{scenario.description}</Typography>
                      <Typography variant="body2">Rendite: {scenario.returnRate}%</Typography>
                      <Typography variant="body2">Inflation: {scenario.inflationRate}%</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="subtitle1">Gespeicherte Szenarien</Typography>
                  <Button variant="contained" onClick={() => calc.setIsScenarioDialogOpen(true)} startIcon={<CompareArrowsIcon />}>Szenario speichern</Button>
                </Box>
                {calc.scenarios.length === 0 && <Typography variant="body2" color="text.secondary">Noch keine Szenarien gespeichert. Szenarien werden automatisch im Browser gespeichert.</Typography>}
                <List>
                  {calc.scenarios.map((scenario) => (
                    <ListItem key={scenario.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, bgcolor: calc.selectedScenarios.includes(scenario.id) ? 'action.selected' : 'background.paper' }}>
                      <ListItemText primary={scenario.name} secondary={`Sparrate: ${formatCurrency(scenario.monthlyInvestment)} | Rendite: ${scenario.returnRate}% | Endalter: ${scenario.targetRetirementAge}`} />
                      <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={() => calc.loadScenario(scenario)}>Laden</Button>
                        <IconButton edge="end" onClick={() => calc.toggleScenarioSelection(scenario.id)} color={calc.selectedScenarios.includes(scenario.id) ? 'primary' : 'default'}><CompareArrowsIcon /></IconButton>
                        <IconButton edge="end" onClick={() => calc.deleteScenario(scenario.id)}><DeleteIcon /></IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          </TabPanel>

          <TabPanel value={calc.tabValue} index={1}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Inflationsberechnung</Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography gutterBottom>Inflationsrate: {calc.inflationRate}%</Typography>
                  {tip("Erwartete durchschnittliche jährliche Inflationsrate")}
                </Box>
                <Slider value={calc.inflationRate} onChange={(_, v) => calc.setInflationRate(v as number)} min={0} max={10} step={0.1} />
              </Box>
              <FormControlLabel control={<Switch checked={calc.showRealValues} onChange={(e) => calc.setShowRealValues(e.target.checked)} />} label="Reale Werte im Graph anzeigen" />
              {calc.showRealValues && lastData && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Ihr Endkapital von {formatCurrency(lastData.totalValue)} entspricht real nur {formatCurrency(lastData.realValue || 0)} in heutiger Kaufkraft.
                  </Typography>
                </Box>
              )}
            </Paper>
          </TabPanel>

          <TabPanel value={calc.tabValue} index={2}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Steuerberechnung</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Steuerregeln: ETF-Gewinne mit 30% Teilfreistellung, Krypto steuerfrei nach 1 Jahr Haltedauer, Immobilien steuerfrei nach 10 Jahren Spekulationsfrist, Cash-Zinsen und Anleihen-Kupons voll steuerpflichtig.
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography gutterBottom>Abgeltungssteuer + Soli: {calc.taxRate}%</Typography>
                  {tip("Steuerbelastung auf Kapitalerträge inkl. Solidaritätszuschlag")}
                </Box>
                <TextField type="number" label="Sparerpauschbetrag" value={calc.taxFreibetrag} onChange={(e) => calc.setTaxFreibetrag(Number(e.target.value))} fullWidth sx={{ mb: 2 }} />
              </Box>
              <FormControlLabel control={<Switch checked={calc.showTaxedValues} onChange={(e) => calc.setShowTaxedValues(e.target.checked)} />} label="Werte nach Steuern im Graph anzeigen" />
              {calc.showTaxedValues && lastData && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Ihr Endkapital nach Steuern beträgt {formatCurrency(lastData.taxedValue || 0)}. Die Steuerersparnis durch den Sparerpauschbetrag beträgt jährlich bis zu {formatCurrency((calc.taxFreibetrag * calc.taxRate) / 100)}.
                  </Typography>
                </Box>
              )}
            </Paper>
          </TabPanel>
        </Grid>
      </Grid>

      <Dialog open={calc.isScenarioDialogOpen} onClose={() => calc.setIsScenarioDialogOpen(false)}>
        <DialogTitle>Szenario speichern</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Szenario-Name" fullWidth value={calc.newScenarioName} onChange={(e) => calc.setNewScenarioName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => calc.setIsScenarioDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={calc.saveScenario} variant="contained" disabled={!calc.newScenarioName}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

/* ── Reusable slider block ── */
interface SliderBlockProps {
  label: string;
  tooltip?: string;
  value: string;
  sliderProps: {
    value: number;
    onChange: (event: Event, value: number | number[]) => void;
    onChangeCommitted: (event: React.SyntheticEvent | Event, value: number | number[]) => void;
    min: number;
    max: number;
    step?: number;
    valueLabelFormat?: (value: number) => string;
    sx?: Record<string, any>;
  };
  tip: (text: string) => React.ReactNode;
}

function SliderBlock({ label, tooltip, value, sliderProps, tip }: SliderBlockProps) {
  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="body1" component="span" color="text.secondary" fontSize={{ xs: 14, sm: 16 }}>{label}</Typography>
        {tooltip && tip(tooltip)}
      </Box>
      <Typography color="primary" variant="h6" gutterBottom fontSize={{ xs: 18, sm: 22 }}>{value}</Typography>
      <Box sx={{ px: 2 }}>
        <Slider {...sliderProps} marks valueLabelDisplay="auto" sx={{ '& .MuiSlider-rail': { opacity: 0.3 }, height: { xs: 2, sm: 4 }, my: 2, ...sliderProps.sx }} />
      </Box>
    </Box>
  );
}

export default ETFCalculator;
